/* eslint-disable object-curly-newline,no-restricted-globals */
/* global React */
import { Box, Grid } from '@mui/material'
import { useMemoRef, useStyles } from '@platform/react/hook'
import { debounce, getFirstItem, PlatformEvent } from 'lib/util'
import { useCallback, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { type Attachment } from '@platform/react/hook/useAttachmentAPI'
import Attachments from 'ui/Element/Message/List/Template/Message/Attachments'
import Content from 'ui/Element/Message/List/Template/Message/Content'
import Uploads from 'ui/Element/Message/List/Template/Message/Uploads'
import Footer from 'ui/Element/Message/List/Template/Message/Footer'

/** whether the message is displayed as primary (own) or secondary (other) */
export type MessageVariant = 'primary' | 'secondary'
/** map of style class names to their generated css classes */
export type MessageClasses = Record<string, string>

// TODO: Should be replaced with Person model type
interface MessagePersonRef {
  /** the person's id */
  key?: string
  /** the person's name values */
  value?: {
    /** the person's full name */
    name?: string
    /** the person's first name */
    firstName?: string
    /** the person's last name */
    lastName?: string
  }
}

// TODO: Should be replaced with Person model type
interface MessageSubmitterRef {
  /** the submitter's id */
  key?: string
  /** the submitter's referenced entities */
  refs?: {
    /** the person the submitter refers to */
    person?: MessagePersonRef[]
  }
}

interface MessageReadReceipt {
  /** whether a read receipt is mandatory */
  mandatory?: boolean
  /** the timestamp at which the message was read */
  timestamp?: string
}

interface MessageValue {
  /** the message text */
  text?: string
  /** the message type */
  type?: string
  /** the message status code */
  status?: number
  /** timestamp at which the message was submitted */
  submitTimestamp?: string
  /** timestamp at which the message was last edited */
  editTimestamp?: string | null
  /** timestamp at which the message was deleted */
  deleteTimestamp?: string | null
  /** the partner that is the source of the message */
  sourcePartner?: string
  /** the message's attachments */
  attachments?: Attachment[]
  /** the message's read receipts */
  read?: MessageReadReceipt[]
}

export interface Message {
  /** the message's id */
  key?: string
  /** the message's id (alias used when rendering) */
  id?: string
  /** the message's access-control levels */
  acl?: number[]
  /** the message's value */
  value: MessageValue
  /** the message's referenced entities */
  refs?: {
    /** the ids of the message's source */
    source?: string[]
    /** the message's submitter */
    submitter?: MessageSubmitterRef[]
  }
}

export interface UploadEntry {
  /** the upload's id */
  key: string
  /** the id of the parent the upload belongs to */
  parent: string
  /** the upload's url */
  url?: string
  /** the upload progress, as a percentage */
  progress?: number
  /** the upload's file name */
  name?: string
  /** the upload's file size */
  size?: number
  /** the upload's mime type */
  mimetype?: string
}

export interface MessageLabels {
  /** translation of english 'me' */
  meLabel?: string
  /** text to be shown while a message is being sent */
  sendingLabel?: string
  /** text to show when a message has been deleted */
  deletedLabel?: string
  /** text to show when a message has been edited */
  editedLabel?: string
  /** text to show for an empty message */
  emptyLabel?: string
}

export interface MessageEvents {
  /** an event handler executed when a message is clicked */
  onClick?: (event: PlatformEvent) => void
  /** an event handler executed when a message is deleted */
  onDelete?: (event: PlatformEvent) => void
  /** an event handler executed when a message changes */
  onChange?: (event: PlatformEvent) => void
}

export interface MessageIcons {
  /** the delete icon element */
  remove?: ReactNode
  /** the edit icon element */
  edit?: ReactNode
}

export interface MessageBaseProps {
  /** the message to render */
  message: Message
  /** whether the message is displayed as primary or secondary */
  variant?: MessageVariant
  /** the id of the current user */
  currentUserId?: string
  /** whether to render the message footer */
  showFooter?: boolean
  /** the labels used by the message */
  labels: MessageLabels
  /** the message's event handlers */
  events?: MessageEvents
  /** the name of the message's attachment group */
  attachmentsGroup?: string
  /** the in-flight upload entries for the message */
  uploads?: UploadEntry[]
}

interface MessageProps extends MessageBaseProps {
  /** the edit/delete icon elements */
  icons: MessageIcons
  /** the message's access-control levels */
  acl?: number[]
}

interface MessageStyleProps {
  /** the message style variant */
  variant?: MessageVariant
  /** whether the edit/delete options are shown */
  hasOptions?: boolean
  /** whether the message is hovered */
  isHovered?: boolean
  /** whether the message has been deleted */
  isDeleted?: boolean
  /** whether the footer is shown */
  showFooter?: boolean
}

/**
 * Returns the Message element's current theme styles.
 *
 * @param theme            the application's current theme
 * @param props            the element's properties
 * @param props.variant    the message style variant
 * @param props.hasOptions whether the edit/delete options are shown
 * @param props.isHovered  whether the message is hovered
 * @param props.isDeleted  whether the message has been deleted
 * @param props.showFooter whether the footer is shown
 * @returns                the Message element's styles
 */
const styles = (theme: any, {
  variant, hasOptions, isHovered, isDeleted, showFooter,
}: MessageStyleProps) => {
  const base: Record<string, any> = {
    row: {
      display: 'flex',
      width: '100%',
    },
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      paddingTop: theme.spacing(0.2),
      paddingBottom: theme.spacing(0.2),
      maxWidth: '100%',
      flex: '0 1 auto',
      ...isDeleted && { minWidth: '50%' },
    },
    content: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      ...hasOptions && { gap: '1.5rem' },
    },
    messageOptions: {
      display: 'flex',
      flexDirection: 'row',
      gap: '0.5rem',
      opacity: isHovered ? '100%' : '0%',
    },
    messageContent: {
      flex: 1,
      maxWidth: '100%',
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'flex-end',
      flexDirection: 'column',
      ...!showFooter && { paddingBottom: '0.5rem' },
    },
    message: {
      padding: '0.5rem',
      borderStyle: 'none',
      borderRadius: '0.5rem',
      boxShadow: '0 0 0.0625rem 0 white inset',
      wordBreak: 'break-word',
    },
    messageEmoji: {
      fontSize: '2rem',
    },
    attachments: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    genericAttachment: {
      position: 'relative',
      marginBottom: '0!important',
      maxWidth: '18.75rem!important', // 300px
      minWidth: '18.75rem!important', // 300px
    },
    attachedImage: {
      padding: '0!important',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: 'contain',
      borderRadius: '0.5rem',
    },
    uploadDelete: {
      position: 'absolute',
      top: '0.375rem',
      right: '0.375rem',
      zIndex: 10,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '1.25rem',
      height: '1.25rem',
      borderRadius: '50%',
      backgroundColor: theme.palette.black.main,
    },
    footer: {
      display: 'flex',
      paddingTop: isDeleted ? '0.25rem' : '0.5rem',
      paddingBottom: '1rem',
    },
    ...theme.custom.message,
  }

  const overrides: Record<string, any> = variant === 'primary'
    ? {
      row: {
        flexDirection: 'row-reverse',
        paddingLeft: 72,
      },
      messageContent: {
        justifyContent: 'flex-end',
      },
      message: {
        alignSelf: 'flex-end',
        backgroundColor: theme.palette.pending[700],
        color: theme.palette.text.inverted,
        '& a': {
          color: `${theme.palette.text.inverted} !important`,
          textDecoration: 'underline!important',
          '&:hover': {
            textDecoration: 'none!important',
          },
        },
      },
      attachments: {
        alignItems: 'flex-end',
      },
      footer: {
        justifyContent: 'flex-end',
      },
      ...theme.custom.messagePrimary,
    }
    : {
      row: {
        flexDirection: 'row',
        paddingRight: 56,
      },
      content: {
        flexDirection: 'row-reverse',
      },
      messageContent: {
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
      },
      attachments: {
        alignItems: 'flex-start',
      },
      message: {
        alignSelf: 'flex-start',
        backgroundColor: theme.palette.gray[960],
        color: theme.palette.black.main,
        '& a': {
          color: '#000!important',
          textDecoration: 'underline!important',
          '&:hover': {
            textDecoration: 'none!important',
          },
        },
      },
      footer: {
        flexDirection: 'row',
      },
      ...theme.custom.messageSecondary,
    }

  return Object.keys(base).reduce<Record<string, any>>((acc, key) => {
    acc[key] = { ...base[key], ...overrides[key] }
    return acc
  }, {})
}

/**
 * Resolves a message submitter's display name from their person ref, falling
 * back to the source partner.
 *
 * @param submitterPerson  the submitter's person reference
 * @param sourcePartner    fallback partner name
 * @returns                the resolved submitter name
 */
const getSubmitterName = (submitterPerson?: MessagePersonRef, sourcePartner?: string): string => {
  const { firstName, lastName, name } = submitterPerson?.value || {}
  return (firstName && lastName && `${firstName} ${lastName}`)
    || name
    || sourcePartner
    || ''
}

/**
 * Message Item.
 *
 * Item used to display any kind of message sent by a user.
 *
 * @param props                  the element's properties
 * @param props.message          the message object
 * @param props.variant          the style variant
 * @param props.currentUserId    the ID of the current user
 * @param props.showFooter       whether to display the footer
 * @param props.labels           translated information labels
 * @param props.events           the message event handlers
 * @param props.attachmentsGroup the attachment group
 * @param props.uploads          files currently being uploaded
 * @returns                      the new Message element
 */
const MessageItem = ({
  message,
  variant = 'secondary',
  currentUserId,
  showFooter,
  labels,
  events,
  attachmentsGroup,
  uploads = [],
  ...props
}: MessageProps) => {
  const { icons, acl = [] } = props
  const { remove, edit } = icons
  const { meLabel, sendingLabel, deletedLabel, editedLabel, emptyLabel } = labels
  const { value, refs = {} } = message
  const {
    text,
    submitTimestamp,
    editTimestamp = null,
    attachments = [],
  } = value
  const submitter = refs.submitter?.[0]
  const {
    person: [submitterPerson] = [],
  } = submitter?.refs || {}

  const hoveredState = useRef(false)
  const [isHovered, setIsHovered] = useState(false)
  const submitterName = getSubmitterName(submitterPerson, value.sourcePartner)
  const authorName = getFirstItem(refs.submitter)?.key === currentUserId ? meLabel : submitterName

  const canDelete = acl.includes(40) || message?.acl?.includes?.(40)
  const canEdit = acl.includes(30) || message?.acl?.includes?.(30)

  const isDeleted = message?.value?.status === 90
  const hasOptions = (canDelete || canEdit) && !isDeleted
  const isEmpty = !isDeleted && !text?.length && !attachments?.length && !uploads?.length

  const classes = useStyles(styles, {
    variant, hasOptions, isHovered, isDeleted, showFooter,
  })() as MessageClasses

  const handleDelete = useCallback((e: MouseEvent<HTMLElement>) => {
    const deleteEvent = new PlatformEvent(e, message)
    events?.onDelete?.(deleteEvent)
  }, [events?.onDelete, message])

  const handleChange = useCallback((e: MouseEvent<HTMLElement>) => {
    const changeEvent = new PlatformEvent(e, message)
    events?.onChange?.(changeEvent)
  }, [events?.onChange, message])

  const setHovered = useCallback(() => {
    setIsHovered(!!hoveredState?.current)
  }, [])
  const debouncedSetHovered = useMemo(() => debounce(setHovered), [setHovered])

  const handleHovered = useCallback((e: MouseEvent<HTMLElement>) => {
    if (e.type === 'mouseenter') hoveredState.current = true
    if (e.type === 'mouseleave') hoveredState.current = false

    debouncedSetHovered()
  }, [debouncedSetHovered])

  return (
    <Grid
      item
      className={classes.row}
    >
      <Grid
        item
        className={classes.wrapper}
      >
        <Box
          className={classes.content}
          onMouseEnter={handleHovered}
          onMouseLeave={handleHovered}
        >
          {!isDeleted && (
            <Box className={classes.messageOptions}>
              {canEdit && (
                <Box
                  onClick={handleChange}
                  sx={{ cursor: 'pointer' }}
                >
                  {edit}
                </Box>
              )}
              {canDelete && (
                <Box
                  onClick={handleDelete}
                  sx={{ cursor: 'pointer' }}
                >
                  {remove}
                </Box>
              )}
            </Box>
          )}
          <Box className={classes.messageContent}>
            {!isDeleted && (
              <Attachments
                data={attachments}
                classes={classes}
                events={events}
                variant={variant}
                attachmentsGroup={attachmentsGroup}
                hidden={!attachments || !attachments.length}
                xs={12}
              />
            )}
            {!isDeleted && (
              <Uploads
                data={uploads}
                classes={classes}
                variant={variant}
              />
            )}
            <Content
              text={text}
              isDeleted={isDeleted}
              isEmpty={isEmpty}
              deletedLabel={deletedLabel}
              emptyLabel={emptyLabel}
              classes={classes}
            />
          </Box>
        </Box>
        <Footer
          authorName={authorName}
          timestamp={submitTimestamp}
          editTimestamp={editTimestamp}
          sendingLabel={sendingLabel}
          meLabel={meLabel}
          editedLabel={editedLabel}
          className={classes.footer}
          hidden={!showFooter}
          variant={variant}
        />
      </Grid>
    </Grid>
  )
}

export default useMemoRef(MessageItem, (props: MessageProps) => [
  props.message?.value,
  props.message?.value?.status,
  props.showFooter,
  props.uploads,
])
